import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import api from "../lib/api";

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};

// No cart size limit - users can add unlimited sites
// Note: Performance may degrade with very large carts (10k+), but that's acceptable
const MAX_CART_SIZE = Infinity;

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('rootseo_cart');
      const parsed = saved ? JSON.parse(saved) : [];
      const list = Array.isArray(parsed) ? parsed : [];
      return list.map(item => ({
        ...item,
        anchor_text: item.anchor_text ?? (Array.isArray(item.keywords) && item.keywords[0]) ?? ""
      }));
    } catch {
      return [];
    }
  });

  const [cartOpen, setCartOpen] = useState(false);
  
  // Use ref to track if we should save (debounce localStorage writes)
  const saveTimeoutRef = useRef(null);

  // Listen for storage changes (e.g., logout clears cart)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'rootseo_cart') {
        if (!e.newValue) {
          // Cart was cleared (logout)
          setCart([]);
          setCartOpen(false);
        } else {
          try {
            const parsed = JSON.parse(e.newValue);
            setCart(Array.isArray(parsed) ? parsed : []);
          } catch {
            setCart([]);
          }
        }
      }
      // Also check if token was removed (logout)
      if (e.key === 'token' && !e.newValue) {
        setCart([]);
        setCartOpen(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Debounced localStorage save to prevent too many writes
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem('rootseo_cart', JSON.stringify(cart));
      } catch (e) {
        // localStorage might be full with large carts
        console.warn('Failed to save cart to localStorage:', e);
      }
    }, 300);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [cart]);

  // Bayat sepet: aktif olmayan site ID'lerini otomatik temizle
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || cart.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/sites/all-ids");
        if (cancelled) return;
        const validIds = new Set(res.data?.ids || []);
        const staleIds = cart.filter((item) => !validIds.has(item.site_id)).map((item) => item.site_id);
        if (staleIds.length > 0) {
          const staleSet = new Set(staleIds);
          setCart((prev) => prev.filter((item) => !staleSet.has(item.site_id)));
        }
      } catch (e) {
        console.warn("Cart stale-id cleanup skipped:", e);
      }
    })();

    return () => { cancelled = true; };
  }, []); // mount only — cart length intentionally excluded to avoid re-fetch loops

  // Create a Set for O(1) lookup performance
  const cartSiteIds = useMemo(() => {
    return new Set(cart.map(item => item.site_id));
  }, [cart]);

  const addToCart = useCallback((site) => {
    if (cartSiteIds.has(site.id)) {
      return false;
    }
    setCart(prev => [...prev, { 
      site_id: site.id, 
      site_name: site.name, 
      site_url: site.url, 
      credit_cost: site.credit_cost, 
      anchor_text: ""
    }]);
    return true;
  }, [cartSiteIds]);

  const addMultipleToCart = useCallback((sites) => {
    const newItems = sites
      .filter(site => !cartSiteIds.has(site.id))
      .map(site => ({
        site_id: site.id,
        site_name: site.name,
        site_url: site.url,
        credit_cost: site.credit_cost,
        anchor_text: ""
      }));
    
    if (newItems.length > 0) {
      setCart(prev => [...prev, ...newItems]);
      return newItems.length;
    }
    return 0;
  }, [cartSiteIds]);

  const removeFromCart = useCallback((siteId) => {
    setCart(prev => prev.filter(item => item.site_id !== siteId));
  }, []);

  // Batch remove multiple items
  const removeMultipleFromCart = useCallback((siteIds) => {
    const idsToRemove = new Set(siteIds);
    setCart(prev => prev.filter(item => !idsToRemove.has(item.site_id)));
  }, []);

  const updateAnchorText = useCallback((siteId, anchorText) => {
    setCart(prevCart => prevCart.map(item => 
      item.site_id === siteId ? { ...item, anchor_text: anchorText } : item
    ));
  }, []);

  const setAllAnchorText = useCallback((anchorText) => {
    setCart(prevCart => prevCart.map(item => ({ ...item, anchor_text: anchorText })));
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  // O(1) lookup using Set
  const isInCart = useCallback((siteId) => {
    return cartSiteIds.has(siteId);
  }, [cartSiteIds]);

  // Memoized total calculation
  const getCartTotal = useCallback(() => {
    return cart.reduce((sum, item) => sum + (item.credit_cost || 0), 0);
  }, [cart]);

  const contextValue = useMemo(() => ({
    cart,
    cartOpen,
    setCartOpen,
    addToCart,
    addMultipleToCart,
    removeFromCart,
    removeMultipleFromCart,
    updateAnchorText,
    setAllAnchorText,
    clearCart,
    isInCart,
    getCartTotal,
    maxCartSize: MAX_CART_SIZE
  }), [
    cart,
    cartOpen,
    addToCart,
    addMultipleToCart,
    removeFromCart,
    removeMultipleFromCart,
    updateAnchorText,
    setAllAnchorText,
    clearCart,
    isInCart,
    getCartTotal
  ]);

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
